import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, Star, CheckCircle, ArrowRight, Filter, Search, ArrowLeft } from 'lucide-react';
import { serviceApiService, Service } from '../services/serviceApi';
import { servicePageConfigApiService, ServicePageContent } from '../services/servicePageConfigApi';
import ServiceContentRenderer from '../components/ServiceContentRenderer';
import Toast from '../components/Toast';
import { useIrisAvatar } from '../hooks/useIrisAvatar';
import '../styles/services.css';

const ServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { avatarUrl } = useIrisAvatar();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [serviceContents, setServiceContents] = useState<{ [key: string]: ServicePageContent[] }>({});
  const serviceRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  // Images premium pour chaque service
  const serviceImages = {
    'web': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    'mobile': 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    'ai': 'https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    'ecommerce': 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    'erp': 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    'consulting': 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    'design': 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    'marketing': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'
  };

  // Unused but kept for future use
  // const gradients = [
  //   'from-blue-500/10 to-purple-500/10',
  //   'from-emerald-500/10 to-teal-500/10',
  //   'from-rose-500/10 to-pink-500/10',
  //   'from-amber-500/10 to-orange-500/10',
  //   'from-indigo-500/10 to-blue-500/10',
  //   'from-violet-500/10 to-purple-500/10'
  // ];

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    // Vérifier s'il y a un paramètre service dans l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const serviceId = urlParams.get('service');
    
    if (serviceId && services.length > 0) {
      // Trouver le service correspondant pour afficher son nom
      const targetService = services.find(s => s.id === serviceId);
      if (targetService) {
        setToastMessage(`Navigation vers le service: ${targetService.name}`);
        setShowToast(true);
        
        // Attendre un peu que le DOM soit rendu puis scroller vers le service
        setTimeout(() => {
          scrollToService(serviceId);
        }, 500);
      }
    }
  }, [services]);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const servicesData = await serviceApiService.getServices();
      const servicesArray = Array.isArray(servicesData) ? servicesData : [];
      setServices(servicesArray);

      // Charger les contenus personnalisés pour chaque service (version publique)
      const contentsMap: { [key: string]: ServicePageContent[] } = {};
      for (const service of servicesArray) {
        try {
          console.log(`📥 Chargement du contenu public pour ${service.name} (ID: ${service.id})`);
          const contents = await servicePageConfigApiService.getServicePageContentsPublic(service.id);
          console.log(`✅ Contenu chargé pour ${service.name}:`, contents.length, 'éléments');
          console.log(`📋 Détail des contenus pour ${service.name}:`, contents);
          
          // Debug spécifique pour les PDF
          const pdfContents = contents.filter(c => c.content_type === 'pdf');
          if (pdfContents.length > 0) {
            console.log(`📄 PDF trouvés pour ${service.name}:`, pdfContents);
          }
          contentsMap[service.id] = contents;
        } catch (err) {
          console.error(`❌ Erreur lors du chargement du contenu pour ${service.name}:`, err);
          contentsMap[service.id] = [];
        }
      }
      setServiceContents(contentsMap);
    } catch (err: any) {
      console.error('Erreur lors du chargement des services:', err);
      setError('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const scrollToService = (serviceId: string) => {
    setSelectedService(serviceId);
    const element = serviceRefs.current[serviceId];
    if (element) {
      // Scroll avec un offset pour tenir compte du header sticky
      const headerOffset = 120;
      const elementPosition = element.offsetTop - headerOffset;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      
      // Ajouter un effet visuel temporaire pour mettre en évidence le service
      element.style.transform = 'scale(1.02)';
      element.style.boxShadow = '0 20px 40px rgba(59, 130, 246, 0.2)';
      
      setTimeout(() => {
        element.style.transform = '';
        element.style.boxShadow = '';
        
        // Nettoyer l'URL après la navigation
        const url = new URL(window.location.href);
        url.searchParams.delete('service');
        window.history.replaceState({}, '', url.toString());
      }, 2000);
    }
  };

  // Unused but kept for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getServiceImage = (serviceName: string, index: number) => {
    const name = serviceName.toLowerCase();
    if (name.includes('web') || name.includes('site')) return serviceImages.web;
    if (name.includes('mobile') || name.includes('app')) return serviceImages.mobile;
    if (name.includes('ia') || name.includes('intelligence') || name.includes('ai')) return serviceImages.ai;
    if (name.includes('ecommerce') || name.includes('boutique') || name.includes('vente')) return serviceImages.ecommerce;
    if (name.includes('erp') || name.includes('gestion') || name.includes('crm')) return serviceImages.erp;
    if (name.includes('consulting') || name.includes('conseil') || name.includes('formation')) return serviceImages.consulting;
    if (name.includes('design') || name.includes('graphique') || name.includes('logo')) return serviceImages.design;
    if (name.includes('marketing') || name.includes('seo') || name.includes('publicité')) return serviceImages.marketing;
    
    // Image par défaut basée sur l'index
    const imageKeys = Object.keys(serviceImages);
    return serviceImages[imageKeys[index % imageKeys.length] as keyof typeof serviceImages];
  };

  const getServiceFeatures = (service: Service) => {
    const features = [
      'Solution sur mesure',
      'Support technique inclus',
      'Livraison garantie',
      'Maintenance comprise'
    ];

    if (service.price_type === 'fixed') {
      features.push('Prix fixe transparent');
    } else if (service.price_type === 'from') {
      features.push('Tarification flexible');
    }

    return features;
  };

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm">Chargement de nos services...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
        <div className="flex items-center justify-center min-h-screen">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-600 text-2xl">⚠️</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Erreur de chargement</h3>
              <p className="text-gray-600 mb-6 text-sm">{error}</p>
              <button
                onClick={loadServices}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      {/* Header avec présentation IRIS */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border-b border-gray-100/50 sticky top-0 z-40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Bouton retour */}
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 transition-all duration-300 border border-blue-200/50"
              >
                <ArrowLeft className="w-5 h-5 text-blue-600" />
              </button>

              {/* Logo et nom cliquables */}
              <button 
                onClick={() => navigate('/')}
                className="flex items-center space-x-4 hover:opacity-80 transition-opacity"
              >
                <img 
                  src="/images/irislogobgremoved.png" 
                  alt="IRIS SOFTECH Logo" 
                  className="w-12 h-12 object-contain"
                />
                <div className="text-left">
                  <h1 className="text-3xl md:text-4xl font-bold text-elegant text-white-gold">
                    IRIS SOFTECH
                  </h1>
                  <p className="text-sm text-gray-600">
                    Nos Services d'Excellence
                  </p>
                </div>
              </button>
            </div>
            
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="lg:hidden p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <Filter className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex gap-8">
          {/* Sidebar avec filtres */}
          <AnimatePresence>
            {(isFilterOpen || (typeof window !== 'undefined' && window.innerWidth >= 1024)) && (
              <motion.div
                initial={{ x: -300, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -300, opacity: 0 }}
                className="w-80 flex-shrink-0"
              >
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100/50 p-6 sticky top-32 max-h-[calc(100vh-8rem)] overflow-y-auto custom-scrollbar">
                  {/* Message d'accueil IRIS */}
                  <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100/50">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                        <img 
                          src={avatarUrl} 
                          alt="IRIS Avatar" 
                          className="w-8 h-8 object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/irisavatar.jpg';
                          }}
                        />
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          <span className="font-semibold text-blue-700">Bonjour !</span> Je suis IRIS, 
                          votre guide pour découvrir nos services. Explorez nos solutions 
                          sur mesure conçues pour votre réussite.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Barre de recherche */}
                  <div className="mb-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Rechercher un service..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-gray-50/50 border border-gray-200/50 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/30 transition-all text-sm"
                      />
                    </div>
                  </div>

                  {/* Liste des services */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                        <Star className="w-4 h-4 text-yellow-500 mr-2" />
                        Services
                      </h3>
                      {filteredServices.length > 5 && (
                        <span className="text-xs text-gray-400 animate-pulse">↕️</span>
                      )}
                    </div>
                    
                    {filteredServices.map((service, index) => (
                      <motion.button
                        key={service.id}
                        onClick={() => scrollToService(service.id)}
                        whileHover={{ x: 4 }}
                        className={`w-full text-left p-3 rounded-xl transition-all group ${
                          selectedService === service.id
                            ? 'bg-gradient-to-r from-blue-500/15 to-purple-500/15 border border-blue-300/60 shadow-sm'
                            : 'hover:bg-gray-50/50 border border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900 group-hover:text-blue-700 transition-colors">
                              {service.name}
                            </h4>
                          </div>
                          <ChevronRight className={`w-4 h-4 text-gray-400 transition-all ${
                            selectedService === service.id ? 'text-blue-600 rotate-90' : 'group-hover:text-blue-600'
                          }`} />
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Contenu principal */}
          <div className="flex-1 min-w-0">
            {filteredServices.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16"
              >
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun service trouvé</h3>
                <p className="text-gray-600 text-sm">
                  Essayez de modifier votre recherche ou parcourez tous nos services.
                </p>
              </motion.div>
            ) : (
              <div className="space-y-6">
                {filteredServices.map((service, index) => (
                  <motion.div
                    key={service.id}
                    ref={(el) => {
                      if (el) {
                        serviceRefs.current[service.id] = el;
                      }
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-gray-100/50 overflow-hidden group hover:shadow-md transition-all duration-300"
                  >
                    {/* Contenu */}
                    <div className="p-6 lg:p-8">
                        <div className="flex items-start justify-between mb-6">
                          <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors">
                              {service.name}
                            </h2>
                          </div>
                        </div>

                        {/* Contenu personnalisé ou description par défaut */}
                        <div className="mb-8">
                          {serviceContents[service.id] && serviceContents[service.id].length > 0 ? (
                            <ServiceContentRenderer contents={serviceContents[service.id]} />
                          ) : (
                            <div className="text-gray-600 leading-relaxed text-sm">
                              {service.description || 'Service professionnel de qualité'}
                            </div>
                          )}
                        </div>

                        {/* Features */}
                        <div className="grid sm:grid-cols-2 gap-3 mb-8">
                          {getServiceFeatures(service).map((feature, idx) => (
                            <div
                              key={idx}
                              className="flex items-center text-sm text-gray-700"
                            >
                              <CheckCircle className="w-4 h-4 text-emerald-500 mr-3 flex-shrink-0" />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-center">
                          <button 
                            onClick={() => navigate('/devisrequest', { state: { selectedService: service } })}
                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 group/btn"
                          >
                            <span className="text-sm">Demander un devis</span>
                            <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                          </button>
                        </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Call to action final */}
            <div className="mt-12 bg-gradient-to-r from-blue-50 via-white to-purple-50 rounded-2xl p-6 lg:p-8 border border-blue-100/50 text-center">
            
              <div className="max-w-2xl mx-auto">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Prêt à démarrer votre projet ?
                </h3>
                <p className="text-gray-600 mb-8 text-sm leading-relaxed">
                  Chez IRIS SOFTECH, chaque projet est unique. Discutons ensemble de vos besoins 
                  pour créer la solution parfaitement adaptée à vos objectifs business.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-8 rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2">
                    <span className="text-sm">Consultation gratuite</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button className="border border-gray-200 text-gray-700 py-3 px-8 rounded-xl font-medium hover:bg-gray-50 transition-all duration-300 text-sm">
                    Voir nos réalisations
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast de notification */}
      <Toast
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        type="info"
        duration={3000}
      />
    </div>
  );
};

export default ServicesPage;
